import React, { useState } from 'react';
import { Box, Button, Stack, TextField, MenuItem, Typography } from '@mui/material';
import Swal from 'sweetalert2';
import Title from '../Home/Title';
import ReCAPTCHA from 'react-google-recaptcha';

const Details = () => {
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone: '',
        profession: '',
        diploma: '',
    });
    const [errors, setErrors] = useState({});
    const [recaptchaToken, setRecaptchaToken] = useState(null);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    // -- Validaciones locales (puedes ajustarlas a tu gusto) --
    const validateFullName = (name) => {
        if (!name.trim()) return 'El nombre completo es obligatorio.';
        if (name.length < 3) return 'El nombre debe tener al menos 3 caracteres.';
        if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(name)) return 'El nombre solo debe contener letras y espacios.';
        return null;
    };

    const validateEmail = (email) => {
        if (!email.trim()) return 'El correo electrónico es obligatorio.';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'El formato del correo electrónico no es válido.';
        return null;
    };

    const validatePhone = (phone) => {
        if (!phone.trim()) return 'El teléfono es obligatorio.';
        if (!/^\d{10}$/.test(phone)) return 'El teléfono debe contener 10 dígitos.';
        return null;
    };

    const validateProfession = (profession) => {
        if (!profession.trim()) return 'La profesión es obligatoria.';
        if (profession.length < 3) return 'La profesión debe tener al menos 3 caracteres.';
        if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(profession)) return 'La profesión solo debe contener letras y espacios.';
        return null;
    };

    const validateDiploma = (diploma) => {
        if (!diploma) return 'Debes seleccionar un diplomado.';
        return null;
    };

    const validateForm = () => {
        const newErrors = {
            fullName: validateFullName(formData.fullName),
            email: validateEmail(formData.email),
            phone: validatePhone(formData.phone),
            profession: validateProfession(formData.profession),
            diploma: validateDiploma(formData.diploma),
        };

        const filteredErrors = Object.fromEntries(
            Object.entries(newErrors).filter(([_, value]) => value !== null)
        );

        setErrors(filteredErrors);
        return Object.keys(filteredErrors).length === 0;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        // 1. Validar campos del formulario
        if (!validateForm()) return;

        // 2. Verificar que el usuario haya pasado reCAPTCHA
        if (!recaptchaToken) {
            Swal.fire({
                icon: 'error',
                title: 'Fallo en reCAPTCHA',
                text: 'Por favor completa la verificación reCAPTCHA.',
            });
            return;
        }

        try {
            // 3. Validar el token de reCAPTCHA en tu captcha server
            const captchaResponse = await fetch('https://captcha.imdituxtla.com.mx/captcha.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({ token: recaptchaToken }),
            });

            const captchaResult = await captchaResponse.json();

            if (!captchaResult.success) {
                Swal.fire({
                    icon: 'error',
                    title: 'Error en reCAPTCHA',
                    text: 'Validación fallida. Por favor inténtalo de nuevo.',
                });
                return;
            }

            // 4. Si el reCAPTCHA se validó con éxito, enviamos los datos del formulario a la base de datos
            const dbResponse = await fetch('https://server.imdituxtla.com.mx/index.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                // Enviamos los datos del formulario en formato JSON
                body: JSON.stringify(formData),
            });

            const result = await dbResponse.json();

            // Puedes ajustar las condiciones según la respuesta de tu servidor
            if (result.success) {
                Swal.fire({
                    icon: 'success',
                    title: '¡Registro exitoso!',
                    text: 'Tus datos han sido registrados con éxito.',
                });
                // Reseteamos los datos y el captcha
                setFormData({
                    fullName: '',
                    email: '',
                    phone: '',
                    profession: '',
                    diploma: '',
                });
                setRecaptchaToken(null);
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error al guardar',
                    text: 'Hubo un problema al guardar tus datos. Intenta más tarde.',
                });
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error de conexión',
                text: 'No se pudo conectar con el servidor. Por favor intenta más tarde.',
            });
        }
    };

    return (
        <Stack
            component="section"
            justifyContent="center"
            alignItems="center"
            sx={{
                minHeight: '100vh',
                paddingTop: '30px',
                backgroundColor: '#f4f5f7',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box
                component="form"
                noValidate
                onSubmit={handleSubmit}
                sx={{
                    px: 4,
                    py: 4,
                    backgroundColor: '#ffffff',
                    borderRadius: 3,
                    boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.1)',
                    width: '100%',
                    maxWidth: 450,
                    textAlign: 'center',
                }}
            >
                <Title
                    text="Registro"
                    textAlign="center"
                    sx={{
                        fontSize: '2rem',
                        fontWeight: 'bold',
                        mb: 2,
                        color: '#2c3636',
                    }}
                />
                <Typography
                    variant="body1"
                    sx={{
                        color: '#2c3636',
                        mb: 1,
                    }}
                >
                    Llena tus datos para registrarte en el diplomado que deseas.
                </Typography>
                <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="fullName"
                    label="Nombre Completo"
                    name="fullName"
                    autoComplete="name"
                    value={formData.fullName}
                    onChange={handleChange}
                    error={!!errors.fullName}
                    helperText={errors.fullName}
                    sx={{
                        mb: 2,
                    }}
                />
                <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label="Correo Electrónico"
                    name="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleChange}
                    error={!!errors.email}
                    helperText={errors.email}
                    sx={{
                        mb: 2,
                    }}
                />
                <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="phone"
                    label="Teléfono"
                    name="phone"
                    autoComplete="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    error={!!errors.phone}
                    helperText={errors.phone}
                    sx={{
                        mb: 2,
                    }}
                />
                <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="profession"
                    label="Profesión"
                    name="profession"
                    value={formData.profession}
                    onChange={handleChange}
                    error={!!errors.profession}
                    helperText={errors.profession}
                    sx={{
                        mb: 2,
                    }}
                />
                <TextField
                    margin="normal"
                    required
                    fullWidth
                    select
                    id="diploma"
                    label="Diplomado de Interés"
                    name="diploma"
                    value={formData.diploma}
                    onChange={handleChange}
                    error={!!errors.diploma}
                    helperText={errors.diploma}
                    sx={{
                        mb: 3,
                    }}
                >
                    <MenuItem value="Diplomado Básico">Diplomado Básico</MenuItem>
                    <MenuItem value="Diplomado Avanzado">Diplomado Avanzado</MenuItem>
                    <MenuItem value="Maestría">Maestría</MenuItem>
                </TextField>
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block',
                        textAlign: 'left',
                        color: 'gray',
                        mb: 3,
                    }}
                >
                    Los campos marcados con * son obligatorios.
                </Typography>
                <Box sx={{ mb: 3 }}>
                    <ReCAPTCHA
                        sitekey="6LegtacqAAAAAMcHNShq1HACOkGtCwDxWHupgBAY"
                        onChange={(token) => setRecaptchaToken(token)}
                        onExpired={() => setRecaptchaToken(null)}
                    />
                </Box>
                <Button
                    variant="contained"
                    fullWidth
                    type="submit"
                    size="large"
                    sx={{
                        px: { xs: 3, md: 4 },
                        py: { xs: 1, md: 1.5 },
                        fontSize: { xs: '0.9rem', md: '1rem' },
                        fontWeight: 600,
                        color: '#fff',
                        backgroundColor: 'rgb(51, 64, 64)',
                        borderRadius: '10px',
                        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            backgroundColor: 'rgb(41, 54, 54)',
                            transform: 'scale(1.05)',
                            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)',
                        },
                    }}
                >
                    Registrar
                </Button>
            </Box>
        </Stack>
    );
};

export default Details;
